import { NON_PRECISE, estCodeBrut, libelle } from './libelles';

export const operatorText = (value) => {
  const text = String(value || '').trim();
  const labels = {
    effet_unique_par_reference_metier: 'Enregistré une seule fois, sans doublon',
    audit_obligatoire: 'Action toujours notée dans l’historique',
    protected_by_server_validation: 'Contrôle automatique',
    stock_movement_ledger: 'Journal du stock central',
    station_sale_receipt: 'Ticket de vente station',
    station_purchase_receipt: 'Réception fournisseur',
    finance_summary_readiness: 'Lecture finance fiable',
    alert_resolution: 'Résolution d’une alerte',
    trip_status_update: 'Mise à jour du trajet',
    invoice_action: 'Facture',
    fuel_request: 'Demande de carburant',
    fuel_issue: 'Sortie de carburant',
    StockMovement: 'Mouvement de stock',
    CREATED: 'Créé',
    EN_ROUTE_TO_BORDER: 'Vers la frontière',
    AT_CUSTOMS_EXIT: 'À la douane de sortie',
    CLEARED_CUSTOMS_EXIT: 'Douane de sortie passée',
    EN_ROUTE_TO_LOADING: 'Vers le chargement',
    LOADING: 'Chargement',
    EN_ROUTE_TO_ENTRY_CUSTOMS: "Vers la douane d'entrée",
    AT_CUSTOMS_ENTRY: "À la douane d'entrée",
    CLEARED_CUSTOMS_ENTRY: "Douane d'entrée passée",
    ARRIVED_AT_DEPOT: 'Arrivé au dépôt',
    EN_ROUTE_TO_DELIVERY: 'Vers la livraison',
    DELIVERED: 'Livré',
    COMPLETED: 'Clôturé',
    CANCELLED: 'Annulé',
  };
  if (labels[text]) return labels[text];
  // Un texte déjà écrit en français passe tel quel; un code brut ne s’affiche jamais.
  return estCodeBrut(text) ? libelle('tripStatus', text, NON_PRECISE) : text;
};

const contract = ({
  action,
  preconditions,
  expected_effect,
  linked_effects,
  idempotency = 'effet_unique_par_reference_metier',
  audit = 'audit_obligatoire',
  failure_message,
  next_step,
}) => ({
  action,
  preconditions,
  expected_effect,
  linked_effects,
  idempotency,
  audit,
  failure_message,
  next_step,
});

export const tripStatusContract = (readiness, targetStatus) => {
  const checklist = readiness?.checklist || [];
  const blockers = checklist
    .filter((item) => !item.ready && item.status === 'blocked')
    .map((item) => item.reason || item.label)
    .filter(Boolean);

  return contract({
    action: 'trip_status_update',
    preconditions: [
      'trajet en cours',
      'statut suivant sélectionné',
      'localisation disponible si exigée',
      ...checklist.filter((item) => item.ready).slice(0, 4).map((item) => item.label),
    ],
    expected_effect: targetStatus
      ? `Faire avancer le trajet vers “${operatorText(targetStatus)}”.`
      : 'Faire avancer le trajet vers le prochain état métier.',
    linked_effects: ['étapes du trajet', 'alertes', 'documents requis', 'contrôles avant départ'],
    failure_message: blockers.length
      ? blockers.join(' · ')
      : 'Action refusée si les pièces, les volumes, le GPS ou les règles des étapes ne sont pas respectés.',
    next_step: readiness?.summary?.next_action || 'Valider uniquement si les contrôles métier sont cohérents.',
  });
};

export const stockMovementContract = ({ isControlFuel, movementType }) => contract({
  action: 'stock_movement_ledger',
  preconditions: [
    'quantité en litres renseignée',
    'origine et destination cohérentes',
    isControlFuel ? 'mouvement rattaché au stock interne' : 'trajet ou commande de référence si disponible',
    movementType === 'LOSS' ? 'écart expliqué dans la note' : 'référence métier traçable',
  ],
  expected_effect: isControlFuel
    ? 'Mettre à jour les cuves internes et valoriser la consommation ou l’écart.'
    : 'Mettre à jour le stock central sans créer de double mouvement.',
  linked_effects: ['stock consolidé', 'coût au litre', 'historique du stock', 'rapport d’exploitation'],
  failure_message: 'Mouvement bloqué si la quantité, la source ou la référence métier est incohérente.',
  next_step: 'Vérifier le journal stock après validation.',
});

/**
 * Un mouvement de stock n'a pas toujours deux extrémités.
 *
 * Exiger une origine **et** une destination pour tout mouvement obligeait
 * l'écran à en inventer: une perte se voyait attribuer « dépôt → dépôt », et
 * le grand livre crédite l'arrivée puis débite le départ — les deux écritures
 * s'annulaient, et le dépôt ne bougeait pas alors qu'il venait de perdre du
 * carburant. Le solde restait juste, parce qu'il vient du signe du type et non
 * des emplacements, mais le solde **par dépôt** était faux.
 *
 * Ce qui sort n'arrive nulle part, ce qui se corrige à la hausse ne vient de
 * nulle part: chaque type dit lui-même de quelles extrémités il a besoin.
 */
const MOUVEMENTS_SANS_DESTINATION = ['LOSS', 'INTERNAL_CONSUMPTION'];
const MOUVEMENTS_SANS_ORIGINE = ['ADJUSTMENT'];

export const stockMovementBlockers = (formData) => {
  const type = formData.movement_type;
  return [
    !formData.quantity_liters ? 'Quantité manquante' : null,
    Number(formData.quantity_liters || 0) <= 0 ? 'Quantité invalide' : null,
    !formData.from_location && !MOUVEMENTS_SANS_ORIGINE.includes(type)
      ? 'Origine manquante'
      : null,
    !formData.to_location && !MOUVEMENTS_SANS_DESTINATION.includes(type)
      ? 'Destination manquante'
      : null,
  ].filter(Boolean);
};

export const stationSaleContract = ({ openShift, saleForm, selectedStationId }) => contract({
  action: 'station_sale_receipt',
  preconditions: [
    'station sélectionnée',
    'cuve sélectionnée',
    'quantité vendue renseignée',
    'prix au litre renseigné',
    openShift ? 'ronde de caisse ouverte' : 'ronde de caisse à contrôler',
  ],
  expected_effect: 'Enregistrer la vente, diminuer la cuve et créer une trace caisse exploitable.',
  linked_effects: ['stock de la station', 'caisse', 'rapport du jour', 'marge du réseau'],
  failure_message: saleBlockers({ openShift, saleForm, selectedStationId }).join(' · ') || 'Vente refusée si les champs métier sont incomplets.',
  next_step: openShift ? 'Clôturer la ronde de caisse en fin de service.' : 'Ouvrir ou rattacher une ronde de caisse avant la clôture.',
});

export const saleBlockers = ({ openShift, saleForm, selectedStationId }) => [
  !selectedStationId ? 'Station manquante' : null,
  !saleForm.tank_id ? 'Cuve manquante' : null,
  !saleForm.quantity_liters ? 'Quantité manquante' : null,
  !saleForm.unit_price ? 'Prix au litre manquant' : null,
  !openShift && !saleForm.shift_id ? 'Ronde de caisse non rattachée' : null,
].filter(Boolean);

export const stationPurchaseContract = ({ purchaseForm, selectedStationId }) => contract({
  action: 'station_purchase_receipt',
  preconditions: ['station sélectionnée', 'cuve sélectionnée', 'fournisseur renseigné', 'quantité et coût au litre renseignés'],
  expected_effect: 'Augmenter le stock de la cuve, valoriser l’achat et alimenter la marge station.',
  linked_effects: ['stock de la station', 'prix moyen', 'rapport du réseau', 'historique des achats'],
  failure_message: purchaseBlockers({ purchaseForm, selectedStationId }).join(' · ') || 'Achat refusé si la réception fournisseur est incomplète.',
  next_step: 'Comparer le coût fournisseur aux ventes et aux seuils de rupture.',
});

export const purchaseBlockers = ({ purchaseForm, selectedStationId }) => [
  !selectedStationId ? 'Station manquante' : null,
  !purchaseForm.tank_id ? 'Cuve manquante' : null,
  !purchaseForm.supplier_name ? 'Fournisseur manquant' : null,
  !purchaseForm.quantity_liters ? 'Quantité manquante' : null,
  !purchaseForm.unit_cost ? 'Coût au litre manquant' : null,
].filter(Boolean);

export const financeReadinessContract = (reconciliations = {}) => {
  const blocked = Number(reconciliations.blocked_reconciliation_count || 0);
  const pending = Number(reconciliations.pending_reconciliation_count || 0);
  return contract({
    action: 'finance_summary_readiness',
    preconditions: ['volumes livrés', 'réconciliations approuvées', 'stock cohérent', 'factures non dupliquées'],
    expected_effect: 'Afficher une marge fiable uniquement sur des volumes et montants contrôlés.',
    linked_effects: ['facturation', 'créances client', 'marge nette', 'rapport de la direction'],
    failure_message: blocked || pending
      ? `${blocked + pending} écart(s) à contrôler. Facturation et paiement autorisés.`
      : 'Aucune alerte finance détectée sur la période.',
    next_step: blocked || pending ? 'Contrôler les écarts en parallèle de la facturation.' : 'Analyser les marges et les encours clients.',
  });
};

export const alertResolutionContract = (event) => contract({
  action: 'alert_resolution',
  preconditions: ['cause comprise', 'impact vérifié', 'responsable identifié'],
  expected_effect: 'Marquer l’alerte comme traitée sans masquer la trace métier.',
  linked_effects: ['historique des actions', 'étapes concernées', 'rapport d’exploitation'],
  failure_message: event?.ui?.action || 'Résolution refusée si la cause métier n’est pas traitée.',
  next_step: 'Accuser réception si elle est prise en charge, résoudre uniquement après correction.',
});

export const fuelRequestContract = (requestForm = {}) => contract({
  action: 'fuel_request',
  preconditions: ['site connu', 'quantité demandée', 'engin ou bénéficiaire si applicable', 'motif opérationnel'],
  expected_effect: 'Créer une demande à valider avant sortie carburant.',
  linked_effects: ['file de validation', 'estimation du coût', 'suivi de la consommation'],
  failure_message: fuelRequestBlockers(requestForm).join(' · ') || 'Demande bloquée si le site ou la quantité manque.',
  next_step: 'Approuver ou refuser avant toute sortie réelle.',
});

export const fuelRequestBlockers = (requestForm = {}) => [
  !requestForm.site_id ? 'Site manquant' : null,
  !requestForm.requested_quantity ? 'Quantité demandée manquante' : null,
].filter(Boolean);

export const fuelIssueContract = (issueForm = {}) => contract({
  action: 'fuel_issue',
  preconditions: ['site connu', 'quantité sortie', 'engin ou bénéficiaire identifié', 'prix au litre si la dépense est suivie'],
  expected_effect: 'Créer la sortie carburant, diminuer le stock interne et valoriser la dépense.',
  linked_effects: ['mouvement de stock', 'coût par site', 'coût par engin', 'rapport PDF'],
  failure_message: fuelIssueBlockers(issueForm).join(' · ') || 'Sortie bloquée si la quantité ou le bénéficiaire est incohérent.',
  next_step: 'Vérifier la dépense calculée et le rapport de consommation.',
});

export const fuelIssueBlockers = (issueForm = {}) => [
  !issueForm.site_id ? 'Site manquant' : null,
  !issueForm.quantity ? 'Quantité sortie manquante' : null,
  !issueForm.asset_id && !issueForm.received_by_name ? 'Engin ou bénéficiaire manquant' : null,
].filter(Boolean);

export const invoiceContract = ({ invoice, formData, mode = 'create' } = {}) => {
  const source = formData || invoice || {};
  const paidOrCancelled = ['PAID', 'CANCELLED'].includes(invoice?.status);
  return contract({
    action: 'invoice_action',
    preconditions: ['client sélectionné', 'date de la facture', 'au moins une ligne facturable', 'total positif'],
    expected_effect: mode === 'payment'
      ? 'Enregistrer le paiement, recalculer le solde et mettre à jour le statut.'
      : 'Créer ou mettre à jour la facture avec lignes, taxes, solde et modèle PDF.',
    linked_effects: ['créance client', 'facture en PDF', 'historique des paiements', 'relance éventuelle'],
    failure_message: invoiceBlockers({ invoice, formData: source, mode }).join(' · ') || (paidOrCancelled ? 'Facture verrouillée.' : 'Aucun blocage métier détecté.'),
    next_step: mode === 'payment' ? 'Vérifier le solde après paiement.' : 'Prévisualiser le document avant envoi ou téléchargement.',
  });
};

export const invoiceBlockers = ({ invoice, formData = {}, mode = 'create' } = {}) => {
  const lines = formData.line_items || [];
  const total = Number(formData.total_amount || 0);
  return [
    !formData.client_id ? 'Client manquant' : null,
    !formData.invoice_date ? 'Date de la facture manquante' : null,
    // Pas d'échéance: le règlement se fait à l'amiable entre les deux parties,
    // rien à saisir ni à exiger.
    lines.length === 0 ? 'Aucune ligne facturable' : null,
    lines.some((line) => !line.description || Number(line.quantity || 0) <= 0 || Number(line.unit_price || 0) < 0) ? 'Ligne incomplète' : null,
    total <= 0 ? 'Total invalide' : null,
    mode === 'edit' && ['PAID', 'CANCELLED'].includes(invoice?.status) ? 'Facture déjà verrouillée' : null,
  ].filter(Boolean);
};

export const invoicePaymentBlockers = ({ invoice, paymentData = {} } = {}) => {
  const amount = Number(paymentData.amount || 0);
  const remaining = Number(invoice?.amount_remaining ?? invoice?.balance_due ?? invoice?.total_amount ?? 0);
  return [
    !invoice ? 'Facture non sélectionnée' : null,
    amount <= 0 ? 'Montant du paiement invalide' : null,
    amount > remaining ? 'Paiement supérieur au solde' : null,
    invoice?.status === 'CANCELLED' ? 'Facture annulée' : null,
  ].filter(Boolean);
};
